import { Item } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  A3InnuvaClassificationTypeDocument,
  A3innuvaClassificationTypeFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { SpanishContractData } from 'modules/contracts/hooks/useFetchSpanishContractData'
import { translate } from 'modules/contracts/lib/translate'
import {
  classificationTypesToOptions,
  fullName,
} from 'modules/payroll_integrations_a3innuva/lib/a3innuvaHelpers'

interface Props {
  form: Form
  translations: { [key: string]: string }
  spanishContractData: SpanishContractData
  legalEntityId: number
}

const Classification = ({ form, translations, spanishContractData, legalEntityId }: Props) => {
  const session = useCurrentSession()
  const classificationCodeName = form.get('es_classification_payroll_integration_name').value
  const classificationCode = form.get('es_classification_payroll_integration_code').value
  const classificationCodeId = form.get('es_classification_id').value
  const spainClassificationCode = classificationCodeId
    ? spanishContractData?.classifications.get(Number(classificationCodeId))
    : null
  const agreementId = form.get('es_agreement_code_payroll_integration_code').value

  const { data: classificationsData, loading } = useQuery(A3InnuvaClassificationTypeDocument, {
    variables: {
      agreementCode: agreementId,
      companyId: session.company.id,
      currentUserId: session.access.userId,
      legalEntityId,
    },
    skip: !agreementId,
  })
  const a3innuvaClassifications =
    classificationsData?.payrollIntegrationsA3innuva.classifications ?? []
  const options = classificationTypesToOptions(a3innuvaClassifications)

  useEffect(() => {
    setCurrentClassificationCode(a3innuvaClassifications)
  }, [agreementId, a3innuvaClassifications])

  const setCurrentClassificationCode = (
    a3Classifications: A3innuvaClassificationTypeFragment[]
  ) => {
    const currentClassificationCode = a3Classifications.find(
      ({ name }) => name === spainClassificationCode?.attributes.name
    )

    form.setValues({
      es_classification_payroll_integration_code: currentClassificationCode?.code,
      es_classification_payroll_integration_name: currentClassificationCode?.name,
    })
  }

  const onClassificationCodeChanged = (item: Item) => {
    const selectedClassificationCode = a3innuvaClassifications.find(({ id }) => id === item.value)

    form.setValues({
      es_classification_payroll_integration_code: selectedClassificationCode?.code,
      es_classification_payroll_integration_name: selectedClassificationCode?.name,
    })
  }

  return (
    <DropdownSearchBar
      name={fullName(classificationCode, classificationCodeName)}
      label={translate(translations, 'classification') || ''}
      onSelect={onClassificationCodeChanged}
      labelNotFound={i18n.t(
        'contract_versions.forms.contract_extra_information.a3innuva.classification_code_not_found'
      )}
      data={options}
      searchLabel={i18n.t(
        'contract_versions.forms.contract_extra_information.a3innuva.search_label'
      )}
      loading={loading}
      popoverWidth="s400"
    />
  )
}

export default observer(Classification)
